import { formatVehicleLabel } from '@/hooks/useVehicles';

export function todayIsoDate() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Strip to 10-digit Indian mobile (6–9 start). Returns '' if invalid length. */
export function normalizeIndianPhone(raw) {
  let digits = String(raw || '').replace(/\D/g, '');
  if (digits.length === 12 && digits.startsWith('91')) digits = digits.slice(2);
  if (digits.length === 11 && digits.startsWith('0')) digits = digits.slice(1);
  if (digits.length > 10) digits = digits.slice(-10);
  return digits;
}

export function isValidIndianPhone(raw) {
  const phone = normalizeIndianPhone(raw);
  return /^[6-9]\d{9}$/.test(phone);
}

export function isValidName(name) {
  const t = String(name || '').trim();
  if (t.length < 2 || t.length > 80) return false;
  if (!/[a-zA-Z]/.test(t)) return false;
  return /^[a-zA-Z\s.'-]+$/.test(t);
}

export function isValidLocation(loc) {
  return String(loc || '').trim().length >= 2;
}

export function isSameLocation(a, b) {
  return String(a || '').trim().toLowerCase() === String(b || '').trim().toLowerCase();
}

export function isPastDate(isoDate) {
  if (!isoDate) return true;
  return isoDate < todayIsoDate();
}

export function getVehicleCapacity(vehicleLabel, vehicles = []) {
  const match = vehicles.find((v) => formatVehicleLabel(v) === vehicleLabel);
  if (match) return match.capacity;
  const parsed = String(vehicleLabel || '').match(/(\d+)\+1/);
  return parsed ? parseInt(parsed[1], 10) : null;
}

export function validateBookingForm(form, { vehicles = [] } = {}) {
  const next = {};
  const pickup = form.pickup?.trim() || '';
  const drop = form.drop?.trim() || '';
  const passengerCount = parseInt(form.passengers, 10) || 0;

  if (!form.trip_type) next.trip_type = 'Trip type is required';
  if (!form.vehicle) next.vehicle = 'Vehicle is required';

  if (!pickup) next.pickup = 'Pickup location is required';
  else if (!isValidLocation(pickup)) next.pickup = 'Enter at least 2 characters for pickup';

  if (!drop) next.drop = 'Drop location is required';
  else if (!isValidLocation(drop)) next.drop = 'Enter at least 2 characters for drop';
  else if (pickup && drop && isSameLocation(pickup, drop)) {
    next.drop = 'Pickup and drop cannot be the same';
  }

  if (!form.date) next.date = 'Pickup date is required';
  else if (isPastDate(form.date)) next.date = 'Pickup date cannot be in the past';

  if (!form.name?.trim()) next.name = 'Name is required';
  else if (!isValidName(form.name)) next.name = 'Enter your full name (at least 2 letters)';

  if (!form.phone?.trim()) next.phone = 'Phone number is required';
  else if (!isValidIndianPhone(form.phone)) next.phone = 'Enter a valid 10-digit mobile number';

  if (!passengerCount || passengerCount < 1) {
    next.passengers = 'Select number of passengers';
  } else if (passengerCount > 20) {
    next.passengers = 'Maximum 20 passengers allowed';
  } else if (form.vehicle) {
    const capacity = getVehicleCapacity(form.vehicle, vehicles);
    if (capacity && passengerCount > capacity) {
      next.passengers = `This vehicle fits up to ${capacity} passengers — choose a larger cab`;
    }
  }

  return next;
}
